import { useCallback, useEffect, useState } from 'react';
import {
  hasAllPermissions,
  ROLE_LABELS,
  ROLES,
  type MembersView,
  type Role,
} from '@icetus/contracts';
import { Badge, Button, Card } from '@icetus/ui';
import {
  ApiError,
  changeMemberRole,
  fetchMembers,
  inviteMember,
  removeMember,
  revokeInvitation,
} from '../api';
import { useConfirm } from '../components/confirm';
import { CAPABILITIES } from '../lib/role-caps';
import { Toaster, useToasts } from '../components/toast';

/**
 * 成员。
 *
 * 这个页面替掉的是一条很尴尬的流程：客户想加个同事，只能给我们发消息，
 * 我们跑 `create-user.ts`。它同时是权限点的第一块试验田 ——
 * **能不能操作全部看服务端给的 `me.permissions`**，这里的按钮只是把它画出来。
 *
 * 三个刻意的取舍：
 *   - 改角色用行内下拉框，不用弹窗。一次只改一个人的一个字段，弹窗要多点两下
 *   - 邀请成功后**一定把链接显示出来**。SES 只能发已审核的模板，没配模板时
 *     信根本没发出去；这时候如果只显示"已发送"，客户会等一个永远不来的邮件
 *   - 自己那一行不给改、不给删。服务端也会拦，但先灰掉比让他撞一次 409 好
 */

const INVITABLE: readonly Role[] = ['admin', 'editor', 'viewer'];

/**
 * 权限表的列顺序：**权限大的在左**。
 *
 * 契约层的 `ROLES` 是按"从小到大"排列的（viewer → owner），那是给判定与
 * 下拉框用的顺序；放到一张对照表上，客户最常看的是"最高那档能干什么"，
 * 反着排一进来就落在最有用的那一列上。
 */
const ROLE_COLUMNS: readonly Role[] = [...ROLES].reverse();

const ROLE_HINT: Record<Role, string> = {
  viewer: '只能看，改不了任何东西',
  editor: '能改页面、商品，能传图；不能发布',
  admin: '能发布、能绑域名；管不了成员与账单',
  owner: '能管成员与账单。至少留一位',
};

export function MembersPage({ tenantId }: { tenantId: string }) {
  const { items: toasts, push: toast, dismiss: dismissToast } = useToasts();
  const { confirm, confirmDialog } = useConfirm();

  const [data, setData] = useState<MembersView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('editor');
  const [invite, setInvite] = useState<{ link: string; mailed: boolean } | null>(null);
  /**
   * 邀请失败**不能写进 `error`**：那个状态是"整页加载失败"，一写就把成员列表、
   * 邀请表单全换掉，只剩一张"重试"卡 —— 而"这个邮箱已经有账号了"是一次正常的
   * 业务拒绝，不是页面坏了。所以单独一份，贴在表单下面。
   */
  const [inviteError, setInviteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await fetchMembers(tenantId));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '成员列表加载失败');
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const canManage = data?.me.permissions.includes('member:manage') ?? false;

  async function onChangeRole(userId: string, next: Role) {
    setBusy(`role:${userId}`);
    try {
      await changeMemberRole(tenantId, userId, next);
      await load();
      toast(`已改为${ROLE_LABELS[next]}`);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : '改角色失败');
    } finally {
      setBusy(null);
    }
  }

  async function onRemove(userId: string, mail: string) {
    const ok = await confirm({
      title: '移除成员',
      message: `移除后 ${mail} 会立刻失去这个站点的访问权。他的账号还在，只是不在这里了。`,
      confirmText: '移除',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(`del:${userId}`);
    try {
      await removeMember(tenantId, userId);
      await load();
      toast('已移除');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : '移除失败');
    } finally {
      setBusy(null);
    }
  }

  async function onInvite() {
    if (!email.trim()) return;
    setBusy('invite');
    setInvite(null);
    setInviteError(null);
    try {
      const result = await inviteMember(tenantId, { email: email.trim(), role });
      setInvite({ link: result.link, mailed: result.mailed });
      setEmail('');
      await load();
    } catch (err) {
      setInviteError(err instanceof ApiError ? err.message : '邀请失败');
    } finally {
      setBusy(null);
    }
  }

  async function onRevoke(id: string) {
    setBusy(`rev:${id}`);
    try {
      await revokeInvitation(tenantId, id);
      await load();
      toast('邀请已撤销，链接立刻失效');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : '撤销失败');
    } finally {
      setBusy(null);
    }
  }

  if (error) {
    return (
      <Card title="成员">
        <p className="muted">{error}</p>
        <Button onClick={() => void load()}>重试</Button>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card title="成员">
        <p className="muted">加载中…</p>
      </Card>
    );
  }

  const seatsFull = data.seats.limit > 0 && data.seats.used >= data.seats.limit;
  const iAmOwner = data.me.role === 'owner';
  /** 除我之外还有几个 owner。决定"我能不能把自己降级" */
  const otherOwners = data.members.filter((m) => m.role === 'owner' && !m.isSelf).length;

  return (
    <div className="stack">
      {confirmDialog}
      <Toaster items={toasts} onDismiss={dismissToast} />

      {canManage ? (
        <Card title="邀请同事">
          <div className="toolbar">
            <input
              className="field"
              type="email"
              placeholder="对方的邮箱"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void onInvite();
              }}
            />
            <select
              className="field"
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
            >
              {INVITABLE.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
            <Button
              disabled={busy === 'invite' || !email.trim() || seatsFull}
              onClick={() => void onInvite()}
            >
              {busy === 'invite' ? '生成中…' : '生成邀请'}
            </Button>
          </div>

          {inviteError ? <p className="error">{inviteError}</p> : null}

          {seatsFull ? (
            <p className="muted">
              当前套餐最多 {data.seats.limit} 个成员，已经满了。升级套餐后可以继续邀请。
            </p>
          ) : (
            <p className="muted">
              邀请只发给还没有 iCetus
              账号的同事：对方打开链接、设个密码开号，验证完邮箱回到这一页确认加入（别去官网注册，那边会多建一个空站）。已有账号的邮箱不能再加入别人的团队——一个账号只属于一个团队。
            </p>
          )}

          {invite ? (
            <div className={`notice notice--${invite.mailed ? 'ok' : 'warn'}`}>
              <span>
                {invite.mailed ? '邀请邮件已发出。' : '邮件没发出去（未配置邀请模板）。'}
                把下面的链接直接发给对方，{7} 天内有效：
                <br />
                <code style={{ wordBreak: 'break-all' }}>{invite.link}</code>
              </span>
              <Button
                variant="ghost"
                onClick={() => {
                  void navigator.clipboard?.writeText(invite.link);
                  toast('链接已复制');
                }}
              >
                复制链接
              </Button>
            </div>
          ) : null}

          {data.invitations.length > 0 ? (
            <table className="table" style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th>待接受</th>
                  <th>角色</th>
                  <th>有效期至</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.invitations.map((i) => (
                  <tr key={i.id}>
                    <td>{i.email}</td>
                    <td>{ROLE_LABELS[i.role]}</td>
                    <td className="muted">{i.expiresAt.slice(0, 10)}</td>
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy === `rev:${i.id}`}
                        onClick={() => void onRevoke(i.id)}
                      >
                        撤销
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </Card>
      ) : (
        <Card title="邀请同事">
          <p className="muted">只有所有者能管成员。需要加人时找负责签约的同事。</p>
        </Card>
      )}

      <Card>
        {/* 卡片头只剩一个计数，留着就是一条空白横带（标题已经撤掉了）。
            压成表格右上角一行小字，省下约 30px 的高度 */}
        <div className="count-line">
          <span className="muted">
            {data.seats.used} / {data.seats.limit > 0 ? data.seats.limit : '不限'} 人
          </span>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>成员</th>
              <th>角色</th>
              <th>加入时间</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => {
              /**
               * 自己那一行只在一种情况下可改：**我是 owner，且还有别的 owner**。
               *
               * 这是"转让所有权"的最后一步。没有它，owner 只能加不能减，
               * 想把公司交给别人时自己永远摘不掉。服务端同样放开了这一种，
               * 并且会拦住"降完一个 owner 都不剩"。
               */
              const selfDowngrade = m.isSelf && iAmOwner && m.role === 'owner' && otherOwners > 0;
              const editable =
                canManage && (m.isSelf ? selfDowngrade : iAmOwner || m.role !== 'owner');
              return (
                <tr key={m.userId}>
                  <td>
                    {/* 名字在前、邮箱在后：认人靠名字，认账号靠邮箱。
                        没填名字时只显示邮箱，不写"未命名"——新同事本来就没填过 */}
                    {m.displayName ? (
                      <div>
                        {m.displayName}
                        {m.isSelf ? <Badge tone="neutral">你</Badge> : null}
                        {m.verified ? null : <Badge tone="warn">邮箱未验证</Badge>}
                        <div className="muted" style={{ fontSize: 12 }}>
                          {m.email}
                        </div>
                      </div>
                    ) : (
                      <div>
                        {m.email}
                        {m.isSelf ? <Badge tone="neutral">你</Badge> : null}
                        {m.verified ? null : <Badge tone="warn">邮箱未验证</Badge>}
                      </div>
                    )}
                  </td>
                  <td>
                    {editable ? (
                      <select
                        className="field"
                        value={m.role}
                        /* 角色能吃多大权限，以前靠表下面那一整段文字解释；
                           那段撤掉之后改成悬停在角色上就地看一句 */
                        title={ROLE_HINT[m.role]}
                        disabled={busy === `role:${m.userId}`}
                        onChange={(e) => void onChangeRole(m.userId, e.target.value as Role)}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABELS[r]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span title={ROLE_HINT[m.role]}>{ROLE_LABELS[m.role]}</span>
                    )}
                  </td>
                  <td className="muted">{m.joinedAt.slice(0, 10)}</td>
                  <td>
                    {canManage && !m.isSelf ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy === `del:${m.userId}`}
                        onClick={() => void onRemove(m.userId, m.email)}
                      >
                        移除
                      </Button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {iAmOwner && otherOwners === 0 ? (
          <p className="muted" style={{ marginTop: 12 }}>
            你现在是唯一的所有者，所以自己这一行不能改。要把公司交给别人，先把对方改成
            「所有者」，改完之后你就能把自己降成其他角色了。
          </p>
        ) : null}
      </Card>

      {/*
        角色权限对照表。
        原来这件事由顶栏那句副标题承担（"编辑能改内容，管理员能发布…"），
        一句话压四个角色，既漏项又记不住。表格横着能比、竖着能查：
        "让他改文案但不能发布"该给什么角色，是拿一行比四列看出来的。
      */}
      <Card title="角色权限" description="改角色只改能做什么，已经登录的人不会被踢下线。">
        <table className="table role-matrix">
          <thead>
            <tr>
              <th>能做什么</th>
              {ROLE_COLUMNS.map((r) => (
                <th key={r}>{ROLE_LABELS[r]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CAPABILITIES.map((cap) => (
              <tr key={cap.label}>
                <td>{cap.label}</td>
                {ROLE_COLUMNS.map((r) => {
                  /**
                   * `hasAllPermissions` 而不是 `some`：标签写的是"改站点信息、
                   * 绑域名、新建站点"三件事，只有三件都行才算这一行成立。
                   * 空数组恒真 —— "查看"本来就不需要权限点，四种角色都有。
                   */
                  const ok = hasAllPermissions(r, cap.permissions);
                  return (
                    <td key={r} className={ok ? 'role-matrix__yes' : 'role-matrix__no'}>
                      <span aria-hidden="true">{ok ? '✓' : '·'}</span>
                      <span className="visually-hidden">{ok ? '可以' : '不可以'}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
